import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { timeEntriesAPI, usersAPI, timeEntryModificationsAPI, monthValidationsAPI, workSchedulesAPI } from '../../services/api';
import { FiClock, FiCalendar, FiUsers, FiDownload, FiLock, FiAlertCircle, FiCheckCircle, FiEye, FiX, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ExportTimeEntriesModal from '../../components/timeEntry/ExportTimeEntriesModal';
import ModificationRequestsPanel from '../../components/timeEntry/ModificationRequestsPanel';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

const TimeEntriesManagement = () => {
  const { addNotification } = useApp();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [timeEntries, setTimeEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = más recientes, 'asc' = más antiguos
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('entries'); // 'entries', 'requests', 'validation'
  const [validationSubTab, setValidationSubTab] = useState('pending'); // 'pending', 'validated'
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingValidationsCount, setPendingValidationsCount] = useState(0);
  const [pendingValidations, setPendingValidations] = useState([]);
  const [validatedMonths, setValidatedMonths] = useState([]);
  const [isLoadingValidations, setIsLoadingValidations] = useState(false);
  const [viewEntriesModal, setViewEntriesModal] = useState(null); // { employee, year, month, entries }
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [workSchedules, setWorkSchedules] = useState([]);

  useEffect(() => {
    loadEmployees();
    loadPendingCounts();
  }, []);

  useEffect(() => {
    loadTimeEntries();
    loadWorkSchedules();
  }, [selectedEmployee, selectedMonth, selectedYear]);

  const loadEmployees = async () => {
    try {
      const usersData = await usersAPI.getAll();
      // Filtrar todos los miembros del establecimiento
      const establishmentMembers = usersData.filter(emp =>
        emp.establishment_id === user.establishment_id
      );
      setEmployees(establishmentMembers);
    } catch (error) {
      console.error('Error loading employees:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar empleados'
      });
    }
  };

  const loadPendingCounts = async () => {
    try {
      // Cargar conteo de solicitudes de modificacion pendientes
      const modificationCount = await timeEntryModificationsAPI.getPendingCount();
      setPendingRequestsCount(modificationCount.count || 0);

      // Cargar validaciones pendientes (buscar en los últimos 12 meses)
      await loadValidationData();
    } catch (error) {
      console.error('Error loading pending counts:', error);
    }
  };

  const loadValidationData = async () => {
    setIsLoadingValidations(true);
    try {
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();

      const pending = [];
      const validated = [];

      // Revisar los últimos 12 meses (excluyendo el mes actual que no se puede validar)
      for (let i = 1; i <= 12; i++) {
        let month = currentMonth - i;
        let year = currentYear;

        if (month <= 0) {
          month += 12;
          year -= 1;
        }

        try {
          const status = await monthValidationsAPI.getStatus(year, month);

          // Separar empleados pendientes y validados (solo los que tienen fichajes)
          const pendingEmployees = status.employees.filter(e => !e.is_validated && e.total_entries > 0);
          const validatedEmployees = status.employees.filter(e => e.is_validated);

          if (pendingEmployees.length > 0) {
            pending.push({
              year,
              month,
              monthName: months.find(m => m.value === month)?.label || '',
              employees: pendingEmployees
            });
          }

          if (validatedEmployees.length > 0) {
            validated.push({
              year,
              month,
              monthName: months.find(m => m.value === month)?.label || '',
              employees: validatedEmployees
            });
          }
        } catch (error) {
          console.error(`Error loading validation for ${month}/${year}:`, error);
        }
      }

      setPendingValidations(pending);
      setValidatedMonths(validated);

      // Calcular total de pendientes
      const totalPending = pending.reduce((sum, p) => sum + p.employees.length, 0);
      setPendingValidationsCount(totalPending);
    } catch (error) {
      console.error('Error loading validation data:', error);
    } finally {
      setIsLoadingValidations(false);
    }
  };

  const loadEmployeeEntries = async (employee, year, month, monthName) => {
    setIsLoadingEntries(true);
    try {
      const entries = await timeEntriesAPI.getByMonth(year, month, { user_id: employee.user_id });
      setViewEntriesModal({
        employee,
        year,
        month,
        monthName,
        entries
      });
    } catch (error) {
      console.error('Error loading employee entries:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los fichajes'
      });
    } finally {
      setIsLoadingEntries(false);
    }
  };

  const loadTimeEntries = async () => {
    setIsLoading(true);
    try {
      const params = selectedEmployee === 'all' ? {} : { user_id: parseInt(selectedEmployee) };
      const response = await timeEntriesAPI.getByMonth(selectedYear, selectedMonth, params);
      setTimeEntries(response);
    } catch (error) {
      console.error('Error loading time entries:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar registros de jornada'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadWorkSchedules = async () => {
    try {
      const schedules = await workSchedulesAPI.getByMonth(selectedYear, selectedMonth);
      setWorkSchedules(schedules);
    } catch (error) {
      console.error('Error loading work schedules:', error);
      setWorkSchedules([]);
    }
  };

  // Agrupar fichajes por empleado y día
  const groupEntriesByEmployeeAndDay = () => {
    const grouped = {};

    timeEntries.forEach(entry => {
      if (!entry.clock_in_time) return;

      const date = format(parseISO(entry.clock_in_time), 'yyyy-MM-dd');
      const key = `${entry.user_id}-${date}`;

      if (!grouped[key]) {
        grouped[key] = {
          user_id: entry.user_id,
          user_name: entry.user_name || 'N/A',
          date: date,
          entries: [],
          totalMinutes: 0,
          estimatedHours: 0
        };

        // Buscar horas estimadas del horario de trabajo
        const schedule = workSchedules.find(
          s => s.employee_id === entry.user_id && s.date === date
        );
        if (schedule) {
          grouped[key].estimatedHours = schedule.hours;
        }
      }

      grouped[key].entries.push(entry);
      grouped[key].totalMinutes += entry.total_minutes || 0;
    });

    // Convertir a array y ordenar por fecha según el orden seleccionado
    return Object.values(grouped).sort((a, b) => {
      if (a.date !== b.date) {
        return sortOrder === 'desc'
          ? b.date.localeCompare(a.date)  // Más recientes primero
          : a.date.localeCompare(b.date); // Más antiguos primero
      }
      return a.user_name.localeCompare(b.user_name);
    });
  };

  const groupedEntries = groupEntriesByEmployeeAndDay();

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      return format(parseISO(dateTimeString), 'dd/MM/yyyy HH:mm:ss', { locale: es });
    } catch (error) {
      return dateTimeString;
    }
  };

  // Función para formatear horas decimales a formato HH:MM
  const formatHours = (decimalHours) => {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const calculateTotals = () => {
    const totalMinutes = timeEntries.reduce((sum, entry) => {
      return sum + (entry.total_minutes || 0);
    }, 0);
    const totalHours = totalMinutes / 60;

    return {
      totalMinutes,
      totalHours: formatHours(totalHours),
      totalDays: (totalHours / 8).toFixed(2),
      entriesCount: timeEntries.filter(e => e.clock_out_time).length
    };
  };

  const totals = calculateTotals();

  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Registros de Jornada</h1>
          <p className="text-gray-600">Visualiza y gestiona los registros de fichaje de tus empleados</p>
        </div>
        <button
          onClick={() => setIsExportModalOpen(true)}
          className="mt-4 sm:mt-0 flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-sm"
        >
          <FiDownload className="mr-2" />
          Exportar PDF
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('entries')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'entries'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiClock className="inline mr-2" />
              Fichajes
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center ${
                activeTab === 'requests'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiAlertCircle className="inline mr-2" />
              Solicitudes de Modificacion
              {pendingRequestsCount > 0 && (
                <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingRequestsCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('validation')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center ${
                activeTab === 'validation'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiCheckCircle className="inline mr-2" />
              Validacion Mensual
              {pendingValidationsCount > 0 && (
                <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingValidationsCount}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'requests' && (
        <ModificationRequestsPanel onRequestsChange={(count) => {
          setPendingRequestsCount(count);
          // Recargar time entries por si se aprobó una modificación
          loadTimeEntries();
        }} />
      )}

      {activeTab === 'validation' && (
        <div className="bg-white rounded-lg shadow-sm">
          {/* Sub-tabs */}
          <div className="border-b border-gray-200 px-4">
            <nav className="flex -mb-px">
              <button
                onClick={() => setValidationSubTab('pending')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center ${
                  validationSubTab === 'pending'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FiClock className="mr-2" />
                Pendientes
                {pendingValidationsCount > 0 && (
                  <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {pendingValidationsCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setValidationSubTab('validated')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center ${
                  validationSubTab === 'validated'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FiCheckCircle className="mr-2" />
                Validados
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {isLoadingValidations ? (
              <div className="flex justify-center items-center py-12">
                <LoadingSpinner />
              </div>
            ) : validationSubTab === 'pending' ? (
              // Pendientes
              pendingValidations.length === 0 ? (
                <div className="text-center py-12">
                  <FiCheckCircle className="mx-auto text-green-500 text-5xl mb-4" />
                  <p className="text-gray-600 font-medium">No hay validaciones pendientes</p>
                  <p className="text-gray-500 text-sm mt-1">Todos los fichajes de meses anteriores estan validados</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {pendingValidations.map((monthData) => (
                    <div key={`${monthData.year}-${monthData.month}`} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-orange-50 px-4 py-3 border-b border-orange-200">
                        <h3 className="font-semibold text-orange-800">
                          {monthData.monthName} {monthData.year}
                          <span className="ml-2 text-sm font-normal text-orange-600">
                            ({monthData.employees.length} empleado{monthData.employees.length !== 1 ? 's' : ''} pendiente{monthData.employees.length !== 1 ? 's' : ''})
                          </span>
                        </h3>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {monthData.employees.map((employee) => (
                          <div key={employee.user_id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <FiUsers className="text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900">{employee.user_name}</p>
                                <p className="text-sm text-gray-500">
                                  {employee.total_entries} fichajes - {Math.floor(employee.total_minutes / 60)}h {employee.total_minutes % 60}m
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {employee.has_pending_modifications && (
                                <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                                  <FiAlertCircle className="inline mr-1" />
                                  Solicitudes pendientes
                                </span>
                              )}
                              <button
                                onClick={() => loadEmployeeEntries(employee, monthData.year, monthData.month, monthData.monthName)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                              >
                                <FiEye className="h-4 w-4" />
                                Ver
                              </button>
                              <button
                                onClick={async () => {
                                  if (employee.has_pending_modifications) {
                                    addNotification({
                                      type: 'warning',
                                      message: 'Resuelve primero las solicitudes de modificacion pendientes'
                                    });
                                    return;
                                  }
                                  try {
                                    await monthValidationsAPI.validate({
                                      user_id: employee.user_id,
                                      year: monthData.year,
                                      month: monthData.month
                                    });
                                    addNotification({
                                      type: 'success',
                                      message: `Fichajes de ${employee.user_name} validados correctamente`
                                    });
                                    loadValidationData();
                                  } catch (error) {
                                    addNotification({
                                      type: 'error',
                                      message: error.response?.data?.detail || 'Error al validar'
                                    });
                                  }
                                }}
                                disabled={employee.has_pending_modifications}
                                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <FiCheckCircle className="h-4 w-4" />
                                Validar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              // Validados
              validatedMonths.length === 0 ? (
                <div className="text-center py-12">
                  <FiClock className="mx-auto text-gray-400 text-5xl mb-4" />
                  <p className="text-gray-600 font-medium">No hay validaciones registradas</p>
                  <p className="text-gray-500 text-sm mt-1">Aun no se han validado fichajes de meses anteriores</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {validatedMonths.map((monthData) => (
                    <div key={`${monthData.year}-${monthData.month}`} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-green-50 px-4 py-3 border-b border-green-200">
                        <h3 className="font-semibold text-green-800">
                          {monthData.monthName} {monthData.year}
                          <span className="ml-2 text-sm font-normal text-green-600">
                            ({monthData.employees.length} empleado{monthData.employees.length !== 1 ? 's' : ''} validado{monthData.employees.length !== 1 ? 's' : ''})
                          </span>
                        </h3>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {monthData.employees.map((employee) => (
                          <div key={employee.user_id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <FiUsers className="text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900">{employee.user_name}</p>
                                <p className="text-sm text-gray-500">
                                  {employee.total_entries} fichajes - {Math.floor(employee.total_minutes / 60)}h {employee.total_minutes % 60}m
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => loadEmployeeEntries(employee, monthData.year, monthData.month, monthData.monthName)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition-colors"
                              >
                                <FiEye className="h-4 w-4" />
                                Ver
                              </button>
                              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                <FiLock className="h-3 w-3" />
                                Validado
                              </span>
                              {employee.validated_at && (
                                <span className="text-xs text-gray-400">
                                  {new Date(employee.validated_at).toLocaleDateString('es-ES')}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {activeTab === 'entries' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Employee Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiUsers className="inline mr-2" />
              Empleado
            </label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos los empleados</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>

          {/* Month Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiCalendar className="inline mr-2" />
              Mes
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiCalendar className="inline mr-2" />
              Año
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Order Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {sortOrder === 'desc' ? <FiArrowDown className="inline mr-2" /> : <FiArrowUp className="inline mr-2" />}
              Ordenar por
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="desc">Más recientes</option>
              <option value="asc">Más antiguos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Horas</p>
              <p className="text-2xl font-bold text-gray-900">{totals.totalHours}</p>
            </div>
            <FiClock className="text-blue-500 text-3xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Días (8h)</p>
              <p className="text-2xl font-bold text-gray-900">{totals.totalDays}</p>
            </div>
            <FiCalendar className="text-green-500 text-3xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Fichajes Completos</p>
              <p className="text-2xl font-bold text-gray-900">{totals.entriesCount}</p>
            </div>
            <FiUsers className="text-purple-500 text-3xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Minutos</p>
              <p className="text-2xl font-bold text-gray-900">{Math.round(totals.totalMinutes)}</p>
            </div>
            <FiClock className="text-orange-500 text-3xl" />
          </div>
        </div>
      </div>

      {/* Time Entries Grouped by Employee and Day */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : groupedEntries.length === 0 ? (
          <div className="text-center py-12">
            <FiClock className="mx-auto text-gray-400 text-5xl mb-4" />
            <p className="text-gray-500">No hay registros de jornada para este período</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {groupedEntries.map((dayGroup) => {
              const dayTotalHours = dayGroup.totalMinutes / 60;
              const difference = dayGroup.estimatedHours > 0
                ? dayTotalHours - dayGroup.estimatedHours
                : null;

              return (
                <div key={`${dayGroup.user_id}-${dayGroup.date}`} className="p-4 hover:bg-gray-50">
                  {/* Header del día */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                        <FiUsers className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{dayGroup.user_name}</p>
                        <p className="text-sm text-gray-500">
                          {format(parseISO(dayGroup.date), "EEEE, d 'de' MMMM", { locale: es })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-right">
                      {/* Horas estimadas */}
                      {dayGroup.estimatedHours > 0 && (
                        <div className="text-sm">
                          <p className="text-gray-500">Estimado</p>
                          <p className="font-medium text-gray-700">{dayGroup.estimatedHours}h</p>
                        </div>
                      )}
                      {/* Acumulado del día */}
                      <div className="text-sm">
                        <p className="text-gray-500">Acumulado</p>
                        <p className="font-bold text-gray-900">{formatDuration(dayGroup.totalMinutes)}</p>
                      </div>
                      {/* Diferencia */}
                      {difference !== null && (
                        <div className="text-sm">
                          <p className="text-gray-500">Diferencia</p>
                          <p className={`font-medium ${difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {difference >= 0 ? '+' : ''}{difference.toFixed(1)}h
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Fichajes del día */}
                  <div className="ml-13 space-y-2">
                    {dayGroup.entries.map((entry, index) => (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-xs font-medium text-gray-400 w-6">#{index + 1}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                              <span className="font-medium text-green-600">
                                {format(parseISO(entry.clock_in_time), 'HH:mm', { locale: es })}
                              </span>
                              <span className="mx-2 text-gray-400">→</span>
                              {entry.clock_out_time ? (
                                <span className="font-medium text-red-600">
                                  {format(parseISO(entry.clock_out_time), 'HH:mm', { locale: es })}
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                  En curso
                                </span>
                              )}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-gray-700 bg-white px-2 py-1 rounded border border-gray-200">
                            {formatDuration(entry.total_minutes)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {entry.is_month_validated && (
                            <span className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              <FiLock className="h-3 w-3" />
                              Validado
                            </span>
                          )}
                          {entry.has_pending_modification && (
                            <span className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                              <FiAlertCircle className="h-3 w-3" />
                              Solicitud
                            </span>
                          )}
                          {entry.notes && (
                            <span className="text-xs text-gray-500 max-w-xs truncate" title={entry.notes}>
                              {entry.notes}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
        </>
      )}

      {/* View Employee Entries Modal */}
      {viewEntriesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Fichajes de {viewEntriesModal.employee.user_name}
                </h2>
                <p className="text-sm text-gray-500">
                  {viewEntriesModal.monthName} {viewEntriesModal.year}
                </p>
              </div>
              <button
                onClick={() => setViewEntriesModal(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FiX className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {isLoadingEntries ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner />
                </div>
              ) : viewEntriesModal.entries.length === 0 ? (
                <div className="text-center py-12">
                  <FiClock className="mx-auto text-gray-400 text-5xl mb-4" />
                  <p className="text-gray-500">No hay fichajes registrados este mes</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex gap-6">
                      <div>
                        <p className="text-sm text-gray-500">Total fichajes</p>
                        <p className="text-lg font-semibold text-gray-900">{viewEntriesModal.entries.length}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Horas totales</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {Math.floor(viewEntriesModal.employee.total_minutes / 60)}h {viewEntriesModal.employee.total_minutes % 60}m
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Entries Table */}
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fecha
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Entrada
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Salida
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Duracion
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Notas
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {viewEntriesModal.entries.map((entry) => (
                          <tr key={entry.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {entry.clock_in_time ? format(parseISO(entry.clock_in_time), 'dd/MM/yyyy', { locale: es }) : '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {entry.clock_in_time ? format(parseISO(entry.clock_in_time), 'HH:mm:ss', { locale: es }) : '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {entry.clock_out_time ? (
                                format(parseISO(entry.clock_out_time), 'HH:mm:ss', { locale: es })
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                  En curso
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {entry.total_minutes != null ? (() => {
                                const totalSeconds = Math.round(entry.total_minutes * 60);
                                const h = Math.floor(totalSeconds / 3600);
                                const m = Math.floor((totalSeconds % 3600) / 60);
                                const s = totalSeconds % 60;
                                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                              })() : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                              {entry.notes || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setViewEntriesModal(null)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportTimeEntriesModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        employees={employees}
      />
    </div>
  );
};

export default TimeEntriesManagement;
